import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import styled from 'styled-components';
import axios from 'axios';

import { DateField } from '../shared/DateField';

// CMBS Waterfall Stress Console (Plan B1, Option 1).
// Three-pane layout: stress controls (L) | recolored capital stack + tranche
// cash-flow grid (C) | trigger-event narrative (R-bottom).
// Reuses CMBSManagementScreen visual language (Section, Table, cushionColor).

// ============ Types — mirror CMBSWaterfallController records ============
interface TrancheRow {
  id: number;
  trancheName: string;
  trancheClass?: string;
  paymentPriority: number;
  balance?: number;
  notionalBalance?: number;
  couponRate?: number;
  isIo?: boolean;
  originalCreditSupportPct?: number;
  currentCreditSupportPct?: number;
}

interface TrancheCashFlow {
  tranche: TrancheRow;
  interestDue?: number;
  interestPaid?: number;
  interestShortfall?: number;
  principalPaid?: number;
  realizedLoss?: number;
  endingBalance?: number;
}

interface WaterfallRun {
  runId: number;
  poolId: number;
  distributionDate: string;
  totalInterestPaid?: number;
  totalPrincipalPaid?: number;
  totalFeesPaid?: number;
  interestShortfall?: number;
  realizedLoss?: number;
  controlClass?: string;
  cashFlows: TrancheCashFlow[];
}

interface RecentRunRow {
  id: number;
  pool_id?: number;
  distribution_date?: string;
  run_kind?: string;
  scenario_label?: string;
  total_interest_paid?: number;
  total_principal_paid?: number;
  realized_loss?: number;
  control_class?: string;
  created_at?: string;
}

// ============ Styled ============
const Section = styled.div`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.85) 0%, rgba(51, 65, 85, 0.85) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.25rem;
`;
const SectionTitle = styled.div`
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1rem;
  @media (max-width: 1280px) { grid-template-columns: 1fr; }
`;
const StressPanel = styled.div`
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;
const FieldLabel = styled.label`
  color: #94a3b8;
  font-size: 0.66rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  span:last-child { color: #f8fafc; font-size: 0.78rem; font-weight: 700; }
`;
const Slider = styled.input`
  width: 100%;
  accent-color: #38bdf8;
`;
const Input = styled.input`
  width: 100%;
  padding: 0.45rem 0.6rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(15, 23, 42, 0.7);
  color: white;
  font-size: 0.82rem;
  outline: none;
  &:focus { border-color: #38bdf8; }
`;
const RunButton = styled.button<{ $loading?: boolean }>`
  margin-top: 0.5rem;
  padding: 0.7rem;
  border-radius: 8px;
  border: none;
  background: ${p => p.$loading
    ? 'linear-gradient(135deg, #64748b 0%, #475569 100%)'
    : 'linear-gradient(135deg, #38bdf8 0%, #0284c7 100%)'};
  color: white;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: ${p => p.$loading ? 'wait' : 'pointer'};
  transition: transform 0.15s;
  &:hover:not(:disabled) { transform: translateY(-1px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const RunsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  max-height: 220px;
  overflow-y: auto;
`;
const RunRow = styled.button<{ $active?: boolean }>`
  background: ${p => p.$active ? 'rgba(56, 189, 248, 0.15)' : 'transparent'};
  border: 1px solid ${p => p.$active ? '#38bdf8' : 'rgba(255,255,255,0.06)'};
  color: #e2e8f0;
  padding: 0.5rem 0.6rem;
  text-align: left;
  border-radius: 6px;
  font-size: 0.72rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  &:hover { border-color: #38bdf8; }
  .label { color: white; font-weight: 600; }
  .meta { color: #94a3b8; font-size: 0.66rem; font-variant-numeric: tabular-nums; }
`;
const ResultsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
const KpiStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.6rem;
`;
const Kpi = styled.div<{ $color?: string }>`
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-left: 3px solid ${p => p.$color || '#38bdf8'};
  border-radius: 8px;
  padding: 0.6rem 0.8rem;
  .lbl { color: #94a3b8; font-size: 0.62rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
  .val { color: white; font-size: 1.05rem; font-weight: 700; margin-top: 2px; font-variant-numeric: tabular-nums; }
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
  th, td { padding: 0.45rem 0.6rem; text-align: left; }
  thead th {
    color: #94a3b8;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 0.62rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
  tbody tr {
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
    color: #e2e8f0;
  }
  td.num { text-align: right; font-variant-numeric: tabular-nums; }
  th.num { text-align: right; }
`;
const TriggerLog = styled.div`
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(245, 158, 11, 0.18);
  border-left: 3px solid #f59e0b;
  border-radius: 8px;
  padding: 0.7rem 0.9rem;
  font-size: 0.78rem;
  color: #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  .item::before { content: '▸ '; color: #f59e0b; }
`;
const Empty = styled.div`
  color: #94a3b8;
  text-align: center;
  padding: 2rem;
  font-size: 0.85rem;
`;
const StackBar = styled.div<{ $h: number; $fill: string; $hover?: boolean }>`
  height: ${p => p.$h}px;
  background: ${p => p.$fill};
  border: 1px solid ${p => p.$hover ? '#fff' : 'rgba(255,255,255,0.08)'};
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0f172a;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  position: relative;
`;

// ============ helpers (parity with CMBSManagementScreen) ============
const fmtCurrency = (v?: number | null) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};
const fmtPct = (v?: number | null, dp = 2) => v == null ? '—' : `${v.toFixed(dp)}%`;
const fmtDate = (s?: string) => s ? new Date(s).toLocaleDateString() : '—';
const cushionColor = (cushion?: number) => {
  if (cushion == null) return '#475569';
  if (cushion >= 5)  return '#10b981';
  if (cushion >= 0)  return '#84cc16';
  if (cushion >= -2) return '#f59e0b';
  return '#ef4444';
};

interface Props {
  poolId: number;
  poolName?: string;
  poolBalance?: number;
}

const CMBSWaterfallSimulator: React.FC<Props> = ({ poolId, poolName, poolBalance }) => {
  // ----- stress controls (numbers, not strings — single source of truth) -----
  const today = getTenantToday();
  const [distributionDate, setDistributionDate] = useState(today);
  const [stressLtv, setStressLtv]           = useState<number>(65);
  const [stressCapRate, setStressCapRate]   = useState<number>(5.5);
  const [stressDefault, setStressDefault]   = useState<number>(2);
  const [stressRecovery, setStressRecovery] = useState<number>(60);
  const [scenarioLabel, setScenarioLabel]   = useState<string>('');

  const [recent, setRecent]   = useState<RecentRunRow[]>([]);
  const [run, setRun]         = useState<WaterfallRun | null>(null);
  const [loading, setLoading] = useState(false);
  const [hover, setHover]     = useState<number | null>(null);

  // -------- recent runs --------
  const fetchRecent = useCallback(async () => {
    try {
      const res = await axios.get(`/api/credit/cmbs/pools/${poolId}/waterfall/runs`, { params: { limit: 10 } });
      setRecent(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load recent waterfall runs', e);
      setRecent([]);
    }
  }, [poolId]);

  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  // -------- run executor --------
  const handleRun = async () => {
    setLoading(true);
    try {
      const body = {
        runKind: 'SCENARIO',
        scenarioLabel: scenarioLabel || `LTV ${stressLtv} / CR ${stressCapRate} / Def ${stressDefault} / Rec ${stressRecovery}`,
        distributionDate,
        daysInPeriod: 30,
        stressLtvPct: stressLtv,
        stressCapRatePct: stressCapRate,
        stressDefaultRatePct: stressDefault,
        stressRecoveryRatePct: stressRecovery,
        createdBy: 'simulator-ui',
      };
      const res = await axios.post(`/api/credit/cmbs/pools/${poolId}/waterfall/run`, body);
      setRun(res.data);
      fetchRecent();
    } catch (e) {
      console.error('Waterfall run failed', e);
      alert('Waterfall run failed — see console.');
    } finally {
      setLoading(false);
    }
  };

  const loadHistorical = async (runId: number) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/credit/cmbs/waterfall/runs/${runId}`);
      // server returns { run, cashFlows } — adapt to local shape
      const rd: any = res.data;
      const adapted: WaterfallRun = {
        runId: rd.run?.id ?? runId,
        poolId: rd.run?.pool_id ?? poolId,
        distributionDate: rd.run?.distribution_date ?? '',
        totalInterestPaid: rd.run?.total_interest_paid,
        totalPrincipalPaid: rd.run?.total_principal_paid,
        totalFeesPaid: rd.run?.total_fees_paid,
        interestShortfall: rd.run?.interest_shortfall,
        realizedLoss: rd.run?.realized_loss,
        controlClass: rd.run?.control_class,
        cashFlows: Array.isArray(rd.cashFlows) ? rd.cashFlows.map((c: any) => ({
          tranche: {
            id: c.tranche_id,
            trancheName: c.tranche_name ?? '',
            trancheClass: c.tranche_class,
            paymentPriority: c.payment_priority ?? 0,
            balance: c.beginning_balance,
            currentCreditSupportPct: c.current_credit_support_pct,
            originalCreditSupportPct: c.original_credit_support_pct,
            isIo: c.is_io,
            couponRate: c.coupon_rate,
          },
          interestDue: c.interest_due,
          interestPaid: c.interest_paid,
          interestShortfall: c.interest_shortfall,
          principalPaid: c.principal_paid,
          realizedLoss: c.realized_loss,
          endingBalance: c.ending_balance,
        })) : [],
      };
      setRun(adapted);
    } catch (e) {
      console.error('Failed to load run detail', e);
    } finally {
      setLoading(false);
    }
  };

  // -------- derived: capital stack with cushion-coloring --------
  const stackRows = useMemo(() => {
    if (!run || run.cashFlows.length === 0) return [] as { cf: TrancheCashFlow; pct: number; cushion: number }[];
    const total = run.cashFlows.reduce((s, c) => s + (c.tranche.balance || 0), 0);
    if (total <= 0) return [];
    return [...run.cashFlows]
      .filter(c => !c.tranche.isIo)
      .sort((a, b) => (a.tranche.paymentPriority ?? 99) - (b.tranche.paymentPriority ?? 99))
      .map(cf => {
        const pct = ((cf.tranche.balance || 0) / total) * 100;
        // cushion proxy: ending balance / starting balance — losses eat directly into cushion
        const start = cf.tranche.balance || 0;
        const end = cf.endingBalance ?? Math.max(0, start - (cf.principalPaid || 0) - (cf.realizedLoss || 0));
        const lossPct = start > 0 ? ((cf.realizedLoss || 0) / start) * 100 : 0;
        const cushion = (cf.tranche.currentCreditSupportPct ?? 0) - lossPct;
        return { cf, pct, cushion };
      });
  }, [run]);

  // -------- derived: trigger-event log (causal narrative) --------
  const triggerLog = useMemo(() => {
    if (!run) return [] as string[];
    const evs: string[] = [];
    if ((run.interestShortfall ?? 0) > 0) {
      evs.push(`Interest shortfall ${fmtCurrency(run.interestShortfall)} — junior tranches deferred this period.`);
    }
    if ((run.realizedLoss ?? 0) > 0) {
      evs.push(`Realized loss ${fmtCurrency(run.realizedLoss)} written down from bottom of capital stack.`);
    }
    run.cashFlows.forEach(cf => {
      if ((cf.interestShortfall ?? 0) > 0) {
        evs.push(`${cf.tranche.trancheName}: interest shortfall ${fmtCurrency(cf.interestShortfall)} (${fmtPct((cf.interestShortfall! / (cf.interestDue || 1)) * 100, 1)} of due).`);
      }
      if ((cf.realizedLoss ?? 0) > 0) {
        evs.push(`${cf.tranche.trancheName}: ${fmtCurrency(cf.realizedLoss)} principal loss recognized.`);
      }
    });
    if (run.controlClass) {
      evs.push(`Control class: ${run.controlClass} (most-subordinate non-defaulted class with positive balance).`);
    }
    if (evs.length === 0) {
      evs.push('No triggers fired this period — all tranches paid full interest with no realized losses.');
    }
    return evs;
  }, [run]);

  return (
    <Section>
      <SectionTitle>🌊 Waterfall Simulator{poolName ? ` · ${poolName}` : ''}</SectionTitle>
      <Grid>
        {/* ============== LEFT: stress panel ============== */}
        <StressPanel>
          <div>
            <FieldLabel>
              <span>Distribution Date</span>
            </FieldLabel>
            <DateField value={distributionDate ?? ''} onChange={iso => setDistributionDate(iso)} />
          </div>

          <div>
            <FieldLabel>
              <span>Stress LTV</span><span>{stressLtv.toFixed(0)}%</span>
            </FieldLabel>
            <Slider type="range" min={40} max={120} step={1}
                    value={stressLtv} onChange={e => setStressLtv(Number(e.target.value))} />
          </div>

          <div>
            <FieldLabel>
              <span>Stress Cap Rate</span><span>{stressCapRate.toFixed(2)}%</span>
            </FieldLabel>
            <Slider type="range" min={3} max={12} step={0.1}
                    value={stressCapRate} onChange={e => setStressCapRate(Number(e.target.value))} />
          </div>

          <div>
            <FieldLabel>
              <span>Stress Default Rate</span><span>{stressDefault.toFixed(1)}%</span>
            </FieldLabel>
            <Slider type="range" min={0} max={20} step={0.1}
                    value={stressDefault} onChange={e => setStressDefault(Number(e.target.value))} />
          </div>

          <div>
            <FieldLabel>
              <span>Recovery Rate</span><span>{stressRecovery.toFixed(0)}%</span>
            </FieldLabel>
            <Slider type="range" min={0} max={100} step={1}
                    value={stressRecovery} onChange={e => setStressRecovery(Number(e.target.value))} />
          </div>

          <div>
            <FieldLabel><span>Scenario Label (optional)</span></FieldLabel>
            <Input type="text" placeholder="e.g., GFC-replay" value={scenarioLabel}
                   onChange={e => setScenarioLabel(e.target.value)} />
          </div>

          <RunButton onClick={handleRun} disabled={loading} $loading={loading}>
            {loading ? 'Running…' : 'Run Stress'}
          </RunButton>

          {recent.length > 0 && (
            <>
              <div style={{ color: '#94a3b8', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '0.4rem' }}>
                Recent Runs
              </div>
              <RunsList>
                {recent.map(r => (
                  <RunRow key={r.id}
                          $active={run?.runId === r.id}
                          onClick={() => loadHistorical(r.id)}
                          title={r.scenario_label || r.run_kind}>
                    <span className="label">
                      {r.scenario_label || r.run_kind || 'RUN'}
                    </span>
                    <span className="meta">
                      {fmtDate(r.distribution_date)} · int {fmtCurrency(r.total_interest_paid)} · loss {fmtCurrency(r.realized_loss)}
                    </span>
                  </RunRow>
                ))}
              </RunsList>
            </>
          )}
        </StressPanel>

        {/* ============== RIGHT: results ============== */}
        <ResultsCol>
          {!run ? (
            <Empty>Run a stress scenario to see tranche cash-flows, capital-stack impact, and trigger events.</Empty>
          ) : (
            <>
              <KpiStrip>
                <Kpi $color="#38bdf8">
                  <div className="lbl">Interest Paid</div>
                  <div className="val">{fmtCurrency(run.totalInterestPaid)}</div>
                </Kpi>
                <Kpi $color="#10b981">
                  <div className="lbl">Principal Paid</div>
                  <div className="val">{fmtCurrency(run.totalPrincipalPaid)}</div>
                </Kpi>
                <Kpi $color="#fbbf24">
                  <div className="lbl">Int. Shortfall</div>
                  <div className="val">{fmtCurrency(run.interestShortfall)}</div>
                </Kpi>
                <Kpi $color="#ef4444">
                  <div className="lbl">Realized Loss</div>
                  <div className="val">{fmtCurrency(run.realizedLoss)}</div>
                </Kpi>
                <Kpi $color="#a78bfa">
                  <div className="lbl">Control Class</div>
                  <div className="val" style={{ fontSize: '0.95rem' }}>{run.controlClass || '—'}</div>
                </Kpi>
                <Kpi $color="#94a3b8">
                  <div className="lbl">Fees Paid</div>
                  <div className="val">{fmtCurrency(run.totalFeesPaid)}</div>
                </Kpi>
              </KpiStrip>

              <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '1rem' }}>
                {/* Capital stack — recolored by per-period cushion impact */}
                <div>
                  <div style={{ color: '#94a3b8', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.4rem' }}>
                    Capital Stack (post-period)
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {stackRows.length === 0 ? <Empty>—</Empty> : stackRows.map((row, i) => {
                      const h = Math.max(20, Math.min(60, row.pct * 4));
                      const fill = cushionColor(row.cushion);
                      return (
                        <StackBar key={row.cf.tranche.id} $h={h} $fill={fill}
                                  $hover={hover === i}
                                  onMouseEnter={() => setHover(i)}
                                  onMouseLeave={() => setHover(null)}>
                          {row.cf.tranche.trancheName} · {row.pct.toFixed(1)}%
                        </StackBar>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.66rem', color: '#cbd5e1' }}>
                    <Legend color="#10b981" label="Cushion ≥ 5%" />
                    <Legend color="#84cc16" label="0–5%" />
                    <Legend color="#f59e0b" label="-2 to 0%" />
                    <Legend color="#ef4444" label="Hard breach" />
                  </div>
                </div>

                {/* Tranche cash-flow table */}
                <div>
                  <div style={{ color: '#94a3b8', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.4rem' }}>
                    Tranche Cash Flows
                  </div>
                  <Table>
                    <thead>
                      <tr>
                        <th>Tranche</th>
                        <th>Cls</th>
                        <th className="num">Pri</th>
                        <th className="num">Beg Bal</th>
                        <th className="num">Int Due</th>
                        <th className="num">Int Paid</th>
                        <th className="num">Shortfall</th>
                        <th className="num">Prin Paid</th>
                        <th className="num">Loss</th>
                        <th className="num">End Bal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...run.cashFlows]
                        .sort((a, b) => (a.tranche.paymentPriority ?? 99) - (b.tranche.paymentPriority ?? 99))
                        .map(cf => {
                          const start = cf.tranche.balance || 0;
                          const end = cf.endingBalance ?? Math.max(0, start - (cf.principalPaid || 0) - (cf.realizedLoss || 0));
                          return (
                            <tr key={cf.tranche.id}>
                              <td><strong>{cf.tranche.trancheName}</strong>{cf.tranche.isIo && <span style={{ color: '#fbbf24', fontSize: '0.62rem', marginLeft: 4 }}>IO</span>}</td>
                              <td>{cf.tranche.trancheClass || '—'}</td>
                              <td className="num">{cf.tranche.paymentPriority ?? '—'}</td>
                              <td className="num">{fmtCurrency(start)}</td>
                              <td className="num">{fmtCurrency(cf.interestDue)}</td>
                              <td className="num">{fmtCurrency(cf.interestPaid)}</td>
                              <td className="num" style={{ color: (cf.interestShortfall ?? 0) > 0 ? '#fca5a5' : undefined }}>
                                {fmtCurrency(cf.interestShortfall)}
                              </td>
                              <td className="num">{fmtCurrency(cf.principalPaid)}</td>
                              <td className="num" style={{ color: (cf.realizedLoss ?? 0) > 0 ? '#fca5a5' : undefined }}>
                                {fmtCurrency(cf.realizedLoss)}
                              </td>
                              <td className="num">{fmtCurrency(end)}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                </div>
              </div>

              <TriggerLog>
                <div style={{ color: '#fbbf24', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Trigger / Event Log
                </div>
                {triggerLog.map((s, i) => <div key={i} className="item">{s}</div>)}
                {poolBalance != null && (
                  <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginTop: '0.3rem' }}>
                    Pool balance reference: {fmtCurrency(poolBalance)} · run kind: SCENARIO · stress LTV {stressLtv.toFixed(0)}% / cap {stressCapRate.toFixed(2)}% / def {stressDefault.toFixed(1)}% / rec {stressRecovery.toFixed(0)}%.
                  </div>
                )}
              </TriggerLog>
            </>
          )}
        </ResultsCol>
      </Grid>
    </Section>
  );
};

const Legend: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <span style={{ width: 9, height: 9, background: color, borderRadius: 2, display: 'inline-block' }} />
    <span>{label}</span>
  </div>
);

export default CMBSWaterfallSimulator;
